import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { UserPlus, Trash2, Edit, Key, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { API, formatApiError, useAuth } from "@/lib/shared";

export const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", role: "staff", staff_id: "" });
  const [tempPassword, setTempPassword] = useState(null);

  const fetchUsers = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/users`); setUsers(res.data); } 
    catch (err) { toast.error("Failed to load users"); } 
    finally { setLoading(false); }
  }, []);

  const fetchStaff = useCallback(async () => {
    try { const res = await axios.get(`${API}/staff`); setStaff(res.data); } catch (_) { /* silent */ }
  }, []);

  useEffect(() => { fetchUsers(); fetchStaff(); }, [fetchUsers, fetchStaff]);

  const openCreate = () => { setEditingUser(null); setFormData({ name: "", email: "", password: "", role: "staff", staff_id: "" }); setModalOpen(true); };
  const openEdit = (u) => { setEditingUser(u); setFormData({ name: u.name, email: u.email, password: "", role: u.role, staff_id: u.staff_id || "" }); setModalOpen(true); };

  const handleSave = async () => {
    try {
      // staff_id only makes sense for non-admin users; wipe it if role is admin.
      const staffLink = formData.role === 'admin' ? null : (formData.staff_id || null);
      if (editingUser) {
        await axios.put(`${API}/admin/users/${editingUser.id}`, { name: formData.name, email: formData.email, role: formData.role, staff_id: staffLink });
        toast.success("User updated");
      } else {
        if (!formData.password) { toast.error("Password required"); return; }
        await axios.post(`${API}/admin/users`, { ...formData, staff_id: staffLink });
        toast.success("User created");
      }
      setModalOpen(false);
      fetchUsers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    try { await axios.delete(`${API}/admin/users/${userId}`); toast.success("User deleted"); fetchUsers(); } 
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleResetPassword = async (userId) => {
    try { const res = await axios.post(`${API}/admin/users/${userId}/reset-password`); setTempPassword(res.data.temporary_password); } 
    catch (err) { toast.error("Failed to reset password"); }
  };

  const toggleStatus = async (u) => {
    try { await axios.put(`${API}/admin/users/${u.id}`, { is_active: !u.is_active }); toast.success(`User ${u.is_active ? 'deactivated' : 'activated'}`); fetchUsers(); } 
    catch (err) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">System Users</h2>
        <Button size="sm" onClick={openCreate} data-testid="add-user-btn"><UserPlus className="w-4 h-4 mr-1" />Add User</Button>
      </div>
      {loading ? <p className="text-muted-foreground">Loading...</p> : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10"><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{u.name}</p>
                    <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>{u.role}</Badge>
                    {u.role !== 'admin' && u.staff_id && (() => {
                      const s = staff.find(x => x.id === u.staff_id);
                      if (!s) return null;
                      const nm = `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Staff';
                      return (
                        <Badge variant="outline" className="text-[10px] border-primary/40 text-primary" data-testid={`user-linked-${u.id}`}>
                          → {nm}
                        </Badge>
                      );
                    })()}
                  </div>
                  <p className="text-sm text-muted-foreground">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={u.is_active} onCheckedChange={() => toggleStatus(u)} disabled={u.id === user?.id} />
                <Button variant="ghost" size="icon" onClick={() => openEdit(u)}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleResetPassword(u.id)}><Key className="w-4 h-4" /></Button>
                {u.id !== user?.id && <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(u.id)}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingUser ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="user-name-input" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="user-email-input" /></div>
            {!editingUser && <div className="space-y-2"><Label>Password</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} data-testid="user-password-input" /></div>}
            <div className="space-y-2"><Label>Role</Label><Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}><SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="admin">Admin</SelectItem><SelectItem value="physician" data-testid="user-role-physician">Physician (MD)</SelectItem><SelectItem value="management" data-testid="user-role-management">Management</SelectItem></SelectContent></Select></div>
            {formData.role === 'staff' && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  Linked nurse record
                  <span className="text-[10px] font-normal text-muted-foreground">(scopes what this user sees)</span>
                </Label>
                <Select value={formData.staff_id || 'none'} onValueChange={(v) => setFormData({...formData, staff_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="user-staff-link-select"><SelectValue placeholder="No link — user sees everything" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="user-staff-link-none">— Not linked (full access) —</SelectItem>
                    {staff.filter(s => s.is_active !== false).map(s => {
                      const displayName = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email || 'Staff';
                      return (
                        <SelectItem key={s.id} value={s.id} data-testid={`user-staff-link-${s.id}`}>
                          {displayName}{s.role ? ` · ${s.role}` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-[10px] italic text-muted-foreground leading-snug">
                  When linked, this user only sees appointments, patients, and visits assigned to that nurse. Admins always see everything.
                </p>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button><Button onClick={handleSave} data-testid="save-user-btn">{editingUser ? "Update" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempPassword} onOpenChange={() => setTempPassword(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Temporary Password</DialogTitle></DialogHeader>
          <div className="bg-muted p-4 rounded-lg font-mono text-center select-all">{tempPassword}</div>
          <DialogFooter><Button onClick={() => { navigator.clipboard.writeText(tempPassword); toast.success("Copied"); }}>Copy</Button><Button variant="outline" onClick={() => setTempPassword(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== DASHBOARD ====================
